import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Badge,
} from "@chakra-ui/react";
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext";
import { fetchSummary, fetchList } from "./useNewData";
import NewDataStepper from "./NewDataStepper";
import UnmatchedHolds from "./UnmatchedHolds";

// Dismiss is per browser session (sessionStorage) — Michael closing it never
// hides it from Helena, and a fresh login shows it again while items are
// pending. Cleared on signIn/signOut in wrappers/UseAuthentication.js.
// See docs/michael/TASK-new-data-tab.md §6 decision 3.
const DISMISS_KEY = "spearbi_newdata_dismissed";

const isDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const setDismissed = () => {
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch (e) {
    // sessionStorage unavailable — the flag is a convenience, never fatal
  }
};

const NewDataPopup = ({ enabled }) => {
  const { selectedSupplier } = useSupplierRetailer();

  const [isOpen, setIsOpen] = useState(false);
  const [storeItems, setStoreItems] = useState([]);
  const [articleItems, setArticleItems] = useState([]);
  const [counts, setCounts] = useState({ store: 0, article: 0 });
  const [regions, setRegions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [heldRows, setHeldRows] = useState(0); // v2 unmatched-import holds

  // MUST key on [enabled]: AuthenticatedLayout initialises isAdmin to false and
  // only sets it in a mount effect — an [] effect would always see false and
  // the popup would never open for anyone.
  useEffect(() => {
    if (!enabled) return;
    if (isDismissed()) return;
    // The demo-video rig signs in as a master with DEMO selected — never
    // auto-open real supplier/store/file names on camera (docs/DEMO-MODE.md).
    // The Imports → New Data tab still shows the queue.
    if (selectedSupplier === "DEMO") return;

    let cancelled = false;

    const run = async () => {
      try {
        const summary = await fetchSummary();
        if (cancelled || !summary || !summary.tableReady) return;
        const pending = summary.pending || {};
        const held = Number(summary.holds?.rows) || 0;
        const totalPending = (Number(pending.store) || 0) + (Number(pending.article) || 0);
        if (totalPending <= 0 && held <= 0) return;
        setHeldRows(held);

        const [stores, articles] = await Promise.all([
          fetchList({ status: "pending", kind: "store" }),
          fetchList({ status: "pending", kind: "article" }),
        ]);
        if (cancelled) return;

        const storeList = Array.isArray(stores) ? stores : [];
        const articleList = Array.isArray(articles) ? articles : [];
        setStoreItems(storeList);
        setArticleItems(articleList);
        setCounts({ store: storeList.length, article: articleList.length });
        setRegions(summary.regions || []);
        setSuppliers(summary.suppliers || []);
        setIsOpen(true);
      } catch (e) {
        // The popup is a convenience — a failed fetch must never break the app.
        console.error("NewDataPopup: failed to load new-data summary", e);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // selectedSupplier is read at run time only — the spec keys this on enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Close = set the session flag AND actually close. (NotificationPopup
  // ignores its onClose — do not copy that pattern.)
  const handleClose = () => {
    setDismissed();
    setIsOpen(false);
  };

  const handleResolved = (kind) => () => {
    setCounts((prev) => ({ ...prev, [kind]: Math.max(0, (prev[kind] || 0) - 1) }));
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New data found in imports</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Tabs isFitted variant="enclosed">
            <TabList>
              <Tab>
                Stores
                <Badge ml={2} borderRadius="full" colorScheme="orange">
                  {counts.store}
                </Badge>
              </Tab>
              <Tab>
                Articles
                <Badge ml={2} borderRadius="full" colorScheme="orange">
                  {counts.article}
                </Badge>
              </Tab>
              {heldRows > 0 && (
                <Tab>
                  Unmatched
                  <Badge ml={2} borderRadius="full" colorScheme="red">
                    {heldRows}
                  </Badge>
                </Tab>
              )}
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <NewDataStepper
                  items={storeItems}
                  kind="store"
                  regions={regions}
                  suppliers={suppliers}
                  onResolved={handleResolved("store")}
                  onDone={handleClose}
                />
              </TabPanel>
              <TabPanel px={0}>
                <NewDataStepper
                  items={articleItems}
                  kind="article"
                  regions={regions}
                  suppliers={suppliers}
                  onResolved={handleResolved("article")}
                  onDone={handleClose}
                />
              </TabPanel>
              {heldRows > 0 && (
                <TabPanel px={0}>
                  <UnmatchedHolds
                    suppliers={suppliers}
                    compact
                    onChanged={async () => {
                      // one vendor-level action can clear hundreds of rows — re-read the real count
                      try {
                        const s = await fetchSummary();
                        setHeldRows(Number(s?.holds?.rows) || 0);
                      } catch (e) {}
                    }}
                  />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NewDataPopup;
